import { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  FaRocket, FaUsers, FaChartLine, FaAward, FaLightbulb, FaHeadset,
  FaArrowRight,
} from 'react-icons/fa';
import MarqueeTicker from '../Common/MarqueeTicker';

gsap.registerPlugin(ScrollTrigger);

/* ─── Subtle dot-grid SVG background ───────────────────────────── */
const DotGrid = () => (
  <svg aria-hidden="true" className="absolute inset-0 w-full h-full pointer-events-none z-0" style={{ opacity: 0.07 }}>
    <defs>
      <pattern id="dotgrid" width="36" height="36" patternUnits="userSpaceOnUse">
        <circle cx="1" cy="1" r="0.9" fill="white" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#dotgrid)" />
  </svg>
);

/* ─── Stagger text variants — simple, no clipPath ───────────────── */
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.07 } } };
const lineUp  = {
  hidden: { opacity: 0, y: 20 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};
const fadeIn  = {
  hidden: { opacity: 0, y: 14 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

/* ─── Stats data (3 animated + 1 static) ───────────────────────── */
const COUNTED = [
  { value: 500, suffix: '+' },
  { value: 340, suffix: '%' },
  { value: 98,  suffix: '%' },
];

const STAT_CARDS = [
  { label: 'Projects\nShipped',        sub: 'Since 2019'       },
  { label: 'Average ROI\nDelivered',   sub: 'Cross-channel avg' },
  { label: 'Client\nRetention Rate',   sub: 'Year-over-year'    },
  { display: '24 hr', label: 'Response\nSLA',   sub: 'Guaranteed'       },
];

/* ─── Advantage cards ───────────────────────────────────────────── */
const ADVANTAGES = [
  { icon: FaChartLine, title: 'Results, Not Reports',
    body: 'Every engagement ships with defined KPIs. We measure in revenue, retention, and reach — never vanity metrics.',
    span: 'lg:col-span-1' },
  { icon: FaUsers, title: 'Senior-Only Execution',
    body: 'No juniors on your account. Every deliverable is owned by certified specialists with 5+ years in-channel.',
    span: 'lg:col-span-1' },
  { icon: FaRocket, title: 'Marketing Meets Engineering',
    body: 'We deploy campaigns backed by custom tracking infrastructure — because marketing without code leaves ROI on the table.',
    span: 'lg:col-span-2' },
  { icon: FaAward, title: 'Vertical Specialization',
    body: 'Healthcare, fintech, SaaS, e-commerce — we speak your compliance language, buyer psychology, and growth levers.',
    span: 'lg:col-span-2' },
  { icon: FaLightbulb, title: 'AI-Augmented Strategy',
    body: 'Proprietary AI tools for keyword clustering, creative scoring, and conversion path modelling — not template generators.',
    span: 'lg:col-span-1' },
  { icon: FaHeadset, title: '24-Hour Response SLA',
    body: 'Dedicated Slack channel, weekly syncs, monthly audits. You always know exactly where every rupee is going.',
    span: 'lg:col-span-1' },
];

const fadeUp = { hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0 } };

/* ══════════════════════════════════════════════════════════════════ */
const WhyChooseUsSection = () => {
  const statsRef = useRef(null);

  /* GSAP counter — fires once when stat enters viewport */
  useEffect(() => {
    if (!statsRef.current) return;
    const ctx = gsap.context(() => {
      COUNTED.forEach((stat, i) => {
        const el = statsRef.current.querySelector(`[data-stat="${i}"]`);
        if (!el) return;
        const obj = { val: 0 };
        gsap.to(obj, {
          val: stat.value,
          duration: 2.6,
          ease: 'power3.out',
          delay: i * 0.15,
          onUpdate() { el.textContent = Math.round(obj.val) + stat.suffix; },
          scrollTrigger: { trigger: el, start: 'top 88%', once: true },
        });
      });
    }, statsRef);
    return () => ctx.revert();
  }, []);

  return (
    <div className="w-full">

      {/* ═══════════════════════════════════════════════════════════
          DARK SECTION — Redesigned
      ═══════════════════════════════════════════════════════════ */}
      <section
        ref={statsRef}
        className="relative bg-[#070707] border-t border-white/[0.06] overflow-hidden"
      >
        {/* Layered backgrounds */}
        <DotGrid />

        {/* Radial glow — behind the stat grid */}
        <div className="absolute right-0 top-0 bottom-0 w-1/2 pointer-events-none z-0"
          style={{ background: 'radial-gradient(ellipse at 70% 50%, rgba(255,255,255,0.035) 0%, transparent 65%)' }} />

        {/* Faint horizontal rule at 50% */}
        <div className="absolute top-1/2 left-0 right-0 h-px bg-white/[0.05] pointer-events-none z-0" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-24 sm:py-36">

          {/* ── Split: Left text | Right 2×2 stats ── */}
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-16 lg:gap-0 items-center">

            {/* LEFT — Heading + description + CTA */}
            <motion.div
              initial="hidden" whileInView="show"
              viewport={{ once: true, margin: '-80px' }}
              variants={stagger}
              className="lg:pr-20 xl:pr-28"
            >
              <motion.span variants={fadeIn}
                className="text-[11px] uppercase tracking-[0.28em] text-white/50 font-semibold block mb-8">
                ✦&nbsp;&nbsp;Our Advantage&nbsp;&nbsp;✦
              </motion.span>

              <div className="overflow-hidden mb-1">
                <motion.h2 variants={lineUp}
                  className="text-4xl sm:text-5xl md:text-[58px] font-normal text-white/70 tracking-tight leading-[1.05]">
                  Not just another
                </motion.h2>
              </div>
              <div className="overflow-hidden mb-10">
                <motion.h2 variants={lineUp}
                  className="text-5xl sm:text-6xl md:text-[72px] font-black text-white tracking-tight leading-[1.0]">
                  <span className="bg-gradient-to-r from-[#FF7332] via-[#FF1E90] to-[#7000FF] bg-clip-text text-transparent inline pb-2">digital agency.</span>
                </motion.h2>
              </div>

              <motion.p variants={fadeIn}
                className="text-base sm:text-lg text-white/70 font-normal leading-relaxed mb-6 max-w-[38ch]">
                We are engineers, strategists, and builders who operate as one integrated unit —
                shipping campaigns and code in the same sprint cycle.
              </motion.p>
              <motion.p variants={fadeIn}
                className="text-sm text-white/50 font-normal leading-relaxed mb-12 max-w-[36ch]">
                Every client gets a senior account manager, a dedicated engineer, and a performance
                analyst — not a rotating roster of juniors.
              </motion.p>

              {/* Feature pills */}
              <motion.div variants={fadeIn} className="flex flex-wrap gap-2 mb-12">
                {['Full-Stack Team', 'AI-Augmented', 'Senior Only', 'Integrated Delivery'].map((t, i) => (
                  <span key={i}
                    className="px-3.5 py-1.5 border border-white/20 rounded-full text-[11px] font-semibold text-white/60 hover:border-white/40 hover:text-white/85 transition-colors duration-200">
                    {t}
                  </span>
                ))}
              </motion.div>

              <motion.div variants={fadeIn}>
                <Link to="/contact"
                  className="inline-flex items-center gap-2.5 px-8 py-4 bg-white text-black rounded-full text-sm font-bold tracking-wide hover:bg-white/90 transition-colors duration-200 group">
                  Start a Project
                  <FaArrowRight size={11} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            </motion.div>

            {/* RIGHT — 2×2 stat bento */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {STAT_CARDS.map((card, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.07, ease: 'easeOut' }}
                  className="relative overflow-hidden rounded-[20px] border border-white/[0.08]
                    hover:border-white/[0.18] transition-colors duration-400 group
                    p-7 sm:p-8 flex flex-col justify-between min-h-[180px] sm:min-h-[210px]"
                >
                  {/* Corner accent lines */}
                  <div className="absolute top-0 left-0 w-8 h-px bg-white/30" />
                  <div className="absolute top-0 left-0 w-px h-8 bg-white/30" />
                  <div className="absolute bottom-0 right-0 w-8 h-px bg-white/10" />
                  <div className="absolute bottom-0 right-0 w-px h-8 bg-white/10" />

                  {/* Hover radial glow */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-[20px]"
                    style={{ background: 'radial-gradient(circle at 30% 40%, rgba(255,255,255,0.05) 0%, transparent 65%)' }} />

                  {/* Number */}
                  <div>
                    {card.display ? (
                      <div className="text-[clamp(2.8rem,5vw,4.5rem)] font-black text-white tabular-nums leading-none mb-3">
                        {card.display}
                      </div>
                    ) : (
                      <div
                        data-stat={i}
                        className="text-[clamp(2.8rem,5vw,4.5rem)] font-black text-white tabular-nums leading-none mb-3"
                      >
                        0{COUNTED[i]?.suffix}
                      </div>
                    )}

                    <p className="text-sm text-white/70 font-normal leading-snug whitespace-pre-line">
                      {card.label}
                    </p>
                  </div>

                  {/* Sub-label */}
                  <p className="text-[10px] text-white/50 uppercase tracking-widest font-semibold">
                    {card.sub}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* ── Divider + Marquee ── */}
          <div className="mt-20 sm:mt-28 border-t border-white/[0.06] pt-8">
            <MarqueeTicker light speed={46} reverse />
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          LIGHT SECTION — Advantage cards
      ═══════════════════════════════════════════════════════════ */}
      <section className="relative bg-[#FAFBF9] border-t border-[#F1F3F1] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-28 sm:py-40 relative z-10">

          <motion.div
            initial="hidden" whileInView="show" viewport={{ once: true, margin: '-80px' }}
            variants={fadeUp} transition={{ duration: 0.6, ease: 'easeOut' }}
            className="mb-16 sm:mb-24"
          >
            <span className="text-[11px] uppercase tracking-[0.28em] text-[#5A635A] font-semibold block mb-6">
              ✦&nbsp;&nbsp;The Cyborg Edge&nbsp;&nbsp;✦
            </span>
            <h2 className="leading-[1.05] tracking-tight max-w-2xl">
              <span className="block text-3xl sm:text-4xl md:text-5xl font-normal text-black/25">Six reasons</span>
              <span className="block text-4xl sm:text-5xl md:text-[64px] font-bold text-black">
                <span className="bg-gradient-to-r from-[#FF7332] via-[#FF1E90] to-[#7000FF] bg-clip-text text-transparent inline pb-2">brands choose us.</span>
              </span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 auto-rows-fr">
            {ADVANTAGES.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={i}
                  initial="hidden" whileInView="show" viewport={{ once: true, margin: '-40px' }}
                  variants={fadeUp}
                  transition={{ duration: 0.75, delay: Math.min(i * 0.06, 0.22), ease: [0.16, 1, 0.3, 1] }}
                  className={`bg-white border border-[#EAECE9] rounded-[24px] p-8 sm:p-9 flex flex-col justify-between
                    group hover:border-[#1C2E1E] transition-colors duration-300 shadow-sm ${item.span}`}
                >
                  <div>
                    <div className="w-11 h-11 bg-[#FAFBF9] border border-[#EAECE9] rounded-xl flex items-center justify-center mb-7
                      group-hover:bg-[#1C2E1E] group-hover:border-[#1C2E1E] transition-colors duration-300">
                      <Icon size={17} className="text-[#1C2E1E] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-semibold text-black mb-3 tracking-tight leading-snug">{item.title}</h3>
                    <p className="text-[#5A635A] text-sm leading-relaxed font-normal">{item.body}</p>
                  </div>
                  <div className="mt-7 pt-5 border-t border-[#F1F3F1] flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-black/0 group-hover:text-[#1C2E1E] transition-colors duration-300">
                    <span>Learn more</span>
                    <FaArrowRight size={9} />
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* CTA row */}
          <motion.div
            initial="hidden" whileInView="show" viewport={{ once: true }}
            variants={fadeUp} transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="mt-14 sm:mt-16 flex flex-col sm:flex-row items-start sm:items-center gap-6"
          >
            <Link to="/contact"
              className="inline-flex items-center gap-2.5 px-8 py-4 bg-[#1C2E1E] text-white rounded-full text-sm font-semibold hover:bg-black transition-colors duration-200 group">
              Start a Project
              <FaArrowRight size={11} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/services"
              className="text-sm font-semibold text-[#1C2E1E] hover:opacity-60 transition-opacity inline-flex items-center gap-2 group">
              Browse All Services
              <FaArrowRight size={10} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WhyChooseUsSection;
